import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Row, Col, Card, CardHeader, CardBody, Button, Label, Input, Spinner, Alert } from 'reactstrap';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import axios from 'axios';
import API_BASE_URL from '../../../Config/api';
import authHeader from '../../Customer/authHeader';

// Custom date-range report: pick a start and end date, review what the range
// covers, then send the Excel report to the existing report recipients.
// Lives next to the automatic weekly / monthly cards and does not touch them.

const MAX_RANGE_DAYS = 366;
const api = (path) => `${API_BASE_URL}/api${path}`;
const errMsg = (e, fallback) => (e.response && e.response.data && e.response.data.error) || fallback;

// ---- date helpers (calendar days only: no time zone drift) ------------------
const pad = (n) => String(n).padStart(2, '0');
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromIso = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return toIso(d) === s ? d : null; // rejects 2026-02-31 and similar
};
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const daysBetween = (a, b) => Math.round((b - a) / 86400000) + 1;
const fmtLong = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

// "Today" in the report timezone, as a local Date at midnight.
function todayInZone(timeZone) {
  try {
    const p = new Intl.DateTimeFormat('en-GB', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
      .formatToParts(new Date()).reduce((acc, x) => ({ ...acc, [x.type]: x.value }), {});
    return new Date(Number(p.year), Number(p.month) - 1, Number(p.day));
  } catch (e) {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), n.getDate());
  }
}

function validate(start, end, today) {
  if (!start && !end) return { field: 'both', message: 'Choose a start date and an end date.' };
  if (!start) return { field: 'start', message: 'Choose a start date.' };
  if (!end) return { field: 'end', message: 'Choose an end date.' };
  if (end < start) return { field: 'end', message: 'The end date cannot be earlier than the start date.' };
  if (end > today) return { field: 'end', message: 'The end date cannot be in the future.' };
  if (daysBetween(start, end) > MAX_RANGE_DAYS) return { field: 'end', message: `The range cannot be longer than ${MAX_RANGE_DAYS} days.` };
  return null;
}

const COUNT_LABELS = [
  ['newOrders', 'New orders'], ['shipped', 'Shipped'], ['delivered', 'Delivered'],
  ['paymentsReceived', 'Payments'], ['creditNotes', 'Credit notes'], ['cancelled', 'Cancelled'],
];

const CustomRangeReport = ({ timezone, recipients, selectedIds, onSent }) => {
  const today = useMemo(() => todayInZone(timezone || 'Europe/London'), [timezone]);
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [touched, setTouched] = useState(false); // show validation only after an attempt
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null); // server-confirmed range + counts
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState(null); // { type: 'success'|'warning'|'danger', message, failures? }
  const outcomeRef = useRef(null);
  const reviewRef = useRef(null);

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const problem = useMemo(() => validate(start, end, today), [start, end, today]);
  const days = start && end && end >= start ? daysBetween(start, end) : null;

  const activeRecipients = useMemo(() => recipients.filter((r) => r.is_active), [recipients]);
  const chosen = useMemo(() => recipients.filter((r) => selectedIds.has(r.id)), [recipients, selectedIds]);
  // Same recipients as the rest of the page: ticked rows if any, otherwise everyone active.
  const targets = chosen.length ? chosen : activeRecipients;

  // Any change to the range invalidates a previous review and message.
  const changeRange = useCallback((s, e) => {
    setStart(s);
    setEnd(e);
    setPreview(null);
    setOutcome(null);
  }, []);

  const presets = useMemo(() => {
    const y = today.getFullYear();
    const m = today.getMonth();
    return [
      { label: 'Last 7 days', s: addDays(today, -6), e: today },
      { label: 'Last 30 days', s: addDays(today, -29), e: today },
      { label: 'This month', s: new Date(y, m, 1), e: today },
      { label: 'Last month', s: new Date(y, m - 1, 1), e: new Date(y, m, 0) },
      { label: 'Year to date', s: new Date(y, 0, 1), e: today },
    ];
  }, [today]);

  const activePreset = presets.find((p) => start && end && toIso(p.s) === toIso(start) && toIso(p.e) === toIso(end));

  const onCalendarChange = ([s, e]) => changeRange(s, e);
  const onTextDate = (which, value) => {
    const d = value ? fromIso(value) : null;
    if (which === 'start') changeRange(d, end);
    else changeRange(start, d);
  };

  const reviewRange = async () => {
    setTouched(true);
    setOutcome(null);
    if (problem) return;
    setPreviewing(true);
    try {
      const res = await axios.post(api('/report-email/custom/preview'), { startDate: toIso(start), endDate: toIso(end) }, authHeader());
      setPreview(res.data);
      setTimeout(() => reviewRef.current && reviewRef.current.focus(), 0);
    } catch (e) {
      setPreview(null);
      setOutcome({ type: 'danger', message: errMsg(e, 'Could not load the preview. Please try again.') });
    } finally {
      setPreviewing(false);
    }
  };

  const showOutcome = (next) => {
    setOutcome(next);
    setTimeout(() => outcomeRef.current && outcomeRef.current.focus(), 0);
  };

  const send = async () => {
    if (!preview || targets.length === 0) return;
    setSending(true);
    setOutcome(null);
    try {
      const res = await axios.post(api('/report-email/custom/send'), {
        startDate: preview.period.startDate,
        endDate: preview.period.endDate,
        ids: chosen.map((r) => r.id),
      }, authHeader());
      const { sent, failed, results } = res.data;
      const failures = (results || []).filter((r) => r.status === 'failed');
      if (failed) {
        showOutcome({ type: 'warning', message: `Sent to ${sent} recipient(s); ${failed} failed.`, failures });
        toast.warn(`Sent ${sent}, failed ${failed}. See the log below.`);
      } else {
        showOutcome({ type: 'success', message: `Report for ${fmtLong(fromIso(preview.period.startDate))} to ${fmtLong(fromIso(preview.period.endDate))} was sent to ${sent} recipient(s).` });
        toast.success(`Custom report sent to ${sent} recipient(s)`);
      }
      setPreview(null);
      if (onSent) onSent();
    } catch (e) {
      const details = e.response && e.response.data && e.response.data.details;
      showOutcome({
        type: 'danger',
        message: `${errMsg(e, 'The report could not be sent.')}${details ? `: ${details}` : ''}. Nothing was sent.`,
      });
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    changeRange(null, null);
    setTouched(false);
  };

  const showProblem = touched && problem;
  const startInvalid = showProblem && (problem.field === 'start' || problem.field === 'both');
  const endInvalid = showProblem && (problem.field === 'end' || problem.field === 'both');

  return (
    <Row>
      <Col sm="12">
        <Card>
          <CardHeader className="pb-0">
            <h5>Custom Date Range Report</h5>
            <p className="text-muted mb-0">
              Pick any start and end date, review the range, then send the Excel report to your report recipients.
              This is a one-off send; it does not change the automatic weekly or monthly schedules.
            </p>
          </CardHeader>
          <CardBody>
            <Row className="g-4">
              <Col lg="7">
                <div className="d-flex flex-wrap gap-2 mb-3" role="group" aria-label="Quick ranges">
                  {presets.map((p) => (
                    <Button key={p.label} size="sm" color="primary" outline={activePreset !== p}
                      aria-pressed={activePreset === p} onClick={() => changeRange(p.s, p.e)}>
                      {p.label}
                    </Button>
                  ))}
                </div>
                <div className="crr-calendar" role="group" style={{ overflowX: 'auto' }}
                  aria-label="Calendar. Use the arrow keys to move between days, then Enter to choose the start date and again for the end date.">
                  <DatePicker
                    inline
                    selectsRange
                    startDate={start}
                    endDate={end}
                    onChange={onCalendarChange}
                    maxDate={today}
                    monthsShown={narrow ? 1 : 2}
                    calendarStartDay={1}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                  />
                </div>
              </Col>

              <Col lg="5">
                <Row className="g-3">
                  <Col xs="6">
                    <Label for="crr-start">Start date</Label>
                    <Input id="crr-start" type="date" value={start ? toIso(start) : ''} max={toIso(today)}
                      invalid={!!startInvalid} aria-describedby={showProblem ? 'crr-error' : undefined}
                      onChange={(e) => onTextDate('start', e.target.value)} />
                  </Col>
                  <Col xs="6">
                    <Label for="crr-end">End date</Label>
                    <Input id="crr-end" type="date" value={end ? toIso(end) : ''} min={start ? toIso(start) : undefined} max={toIso(today)}
                      invalid={!!endInvalid} aria-describedby={showProblem ? 'crr-error' : undefined}
                      onChange={(e) => onTextDate('end', e.target.value)} />
                  </Col>
                </Row>

                <div className="mt-3 p-3 rounded border bg-light" aria-live="polite" aria-atomic="true">
                  <div className="text-muted small text-uppercase">Selected range</div>
                  {start && end && !problem ? (
                    <div className="fs-6 fw-semibold">
                      {fmtLong(start)} &ndash; {fmtLong(end)}
                      <span className="text-muted fw-normal"> &middot; {days} day{days === 1 ? '' : 's'}</span>
                    </div>
                  ) : start && !end ? (
                    <div>{fmtLong(start)} &ndash; <span className="text-muted">choose an end date</span></div>
                  ) : (
                    <div className="text-muted">{start && end ? 'Invalid range' : 'No dates chosen yet'}</div>
                  )}
                  <div className="text-muted small mt-1">Timezone: {timezone || 'Europe/London'}</div>
                </div>

                {showProblem && (
                  <Alert color="danger" className="mt-3 mb-0 py-2" id="crr-error" role="alert">{problem.message}</Alert>
                )}

                <div className="d-flex flex-wrap gap-2 mt-3">
                  <Button color="primary" onClick={reviewRange} disabled={previewing || sending}>
                    {previewing ? (<><Spinner size="sm" className="me-2" />Checking...</>) : 'Review range'}
                  </Button>
                  <Button color="secondary" outline onClick={reset} disabled={previewing || sending || (!start && !end)}>Clear</Button>
                </div>
              </Col>
            </Row>

            {preview && (
              <section className="mt-4 p-3 border rounded" tabIndex={-1} ref={reviewRef} aria-labelledby="crr-review-title">
                <h6 id="crr-review-title" className="mb-1">Review before sending</h6>
                <p className="mb-3">
                  Report for <strong>{fmtLong(fromIso(preview.period.startDate))}</strong> to <strong>{fmtLong(fromIso(preview.period.endDate))}</strong>
                  {' '}({preview.period.days} day{preview.period.days === 1 ? '' : 's'}). Activity in this range:
                </p>
                <Row className="g-2 mb-3">
                  {COUNT_LABELS.map(([key, label]) => (
                    <Col xs="6" md="4" xl="2" key={key}>
                      <div className="border rounded text-center py-2">
                        <div className="fs-5 fw-semibold">{preview.counts[key]}</div>
                        <div className="text-muted small">{label}</div>
                      </div>
                    </Col>
                  ))}
                </Row>
                {targets.length === 0 ? (
                  <Alert color="warning" className="mb-3" role="alert">
                    There are no active recipients to send to. Add or activate a recipient below first.
                  </Alert>
                ) : (
                  <p className="mb-3">
                    Will be emailed to <strong>{targets.length}</strong> recipient{targets.length === 1 ? '' : 's'}
                    {chosen.length ? ' (the rows you ticked below)' : ' (all active recipients; tick rows below to choose specific people)'}:
                    {' '}<span className="text-muted">{targets.slice(0, 3).map((r) => r.email).join(', ')}{targets.length > 3 ? ` and ${targets.length - 3} more` : ''}</span>
                  </p>
                )}
                <p className="text-muted small">The Excel attachment also includes the current snapshot sheets (awaiting action, in production, status breakdown), which always show today's position.</p>
                <div className="d-flex flex-wrap gap-2">
                  <Button color="success" onClick={send} disabled={sending || targets.length === 0}>
                    {sending ? (<><Spinner size="sm" className="me-2" />Sending...</>) : `Send report to ${targets.length} recipient${targets.length === 1 ? '' : 's'}`}
                  </Button>
                  <Button color="secondary" outline onClick={() => setPreview(null)} disabled={sending}>Change dates</Button>
                </div>
              </section>
            )}

            <div aria-live="polite">
              {outcome && (
                <Alert color={outcome.type} className="mt-4 mb-0" tabIndex={-1} innerRef={outcomeRef}
                  role={outcome.type === 'danger' ? 'alert' : 'status'}>
                  {outcome.message}
                  {outcome.failures && outcome.failures.length > 0 && (
                    <ul className="mb-0 mt-2">
                      {outcome.failures.map((f) => <li key={f.email}>{f.email}: {f.error}</li>)}
                    </ul>
                  )}
                </Alert>
              )}
            </div>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default CustomRangeReport;
